import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import Calculator from '../components/Calculator';
import FocusPanel from '../components/FocusPanel';
import FAQ, { FaqJsonLd } from '../components/FAQ';
import { TrustBadges } from '../components/TrustBadges';
import { SEO_PAGES, FEATURED_SEO_LIST as SEO_LIST } from '../lib/seoPages';
import { SEO_CONTENT } from '../lib/seoContent';
import { PRESETS, FUEL_DEFAULTS } from '../lib/pendelLogic';
export default function SeoPage() {
  const { slug } = useParams();
  const page = SEO_PAGES[slug];

  const preset = page?.preset || 'mittel';
  const initial = {
    ...PRESETS[preset],
    fuel_price: FUEL_DEFAULTS[PRESETS[preset].fuel_type],
  };
  // Hooks must be called unconditionally before any early return.
  const [sharedInput, setSharedInput] = useState(initial);

  if (!page) return <Navigate to="/" replace />;

  const seoContent = SEO_CONTENT[slug];

  return (
    <main data-testid={`seo-page-${slug}`}>
      <Helmet>
        <title>{page.meta_title}</title>
        <meta name="description" content={page.meta_description} />
        <meta property="og:title" content={page.meta_title} />
        <meta property="og:description" content={page.meta_description} />
        <link rel="canonical" href={`/${page.slug}`} />
      </Helmet>
      <FaqJsonLd />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-6">
        <div className="text-xs uppercase tracking-widest text-neutral-500">{page.keyword}</div>
        <h1
          className="font-display text-5xl md:text-7xl tracking-tighter mt-2 max-w-4xl leading-[0.95]"
          data-testid="seo-h1"
        >
          {page.h1}
        </h1>
        <p className="font-display text-2xl md:text-3xl mt-4 max-w-3xl text-neutral-700 tracking-tight">
          {page.headline}
        </p>
        <p className="mt-5 text-base text-neutral-700 max-w-2xl leading-relaxed">{page.intro}</p>
        <TrustBadges className="mt-6" />
      </section>

      {/* Slug-specific FOCUS panel */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <FocusPanel variant={page.variant} baseInput={sharedInput} city={page.city} />
      </section>

      <section
        id="calculator"
        className="max-w-7xl mx-auto px-6 md:px-12 mt-16 scroll-mt-20"
        data-testid="seo-calc-section"
      >
        <div className="text-xs uppercase tracking-widest text-neutral-500">Vollständiger Rechner</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter mt-2 mb-6">
          Alle Eingaben anpassen
        </h2>
        <Calculator
          initialPreset={preset}
          showJobSwitch={!!page.showJobSwitch}
          onInputChange={setSharedInput}
        />
      </section>

      {/* Long-form SEO content */}
      {seoContent?.sections?.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 md:px-12 mt-24" data-testid="seo-longform">
          <article className="space-y-12">
            {seoContent.sections.map((s, i) => (
              <div key={i} data-testid={`seo-content-${i}`}>
                <h2 className="font-display text-3xl md:text-4xl tracking-tighter">{s.h}</h2>
                {s.p && <p className="mt-4 text-neutral-700 leading-relaxed">{s.p}</p>}
                {s.list && (
                  <ul className="mt-4 space-y-2 list-disc pl-6 text-neutral-700 leading-relaxed">
                    {s.list.map((item, k) => (
                      <li key={k}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </article>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Mehr Rechner</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">Auch interessant</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 mt-6 grid-border">
          {SEO_LIST.filter((p) => p.slug !== slug)
            .slice(0, 3)
            .map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`seo-related-${p.slug}`}
                className="p-6 border-neutral-200 [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0 md:[&:not(:last-child)]:border-r hover:bg-neutral-50"
              >
                <div className="font-display text-xl">{p.h1}</div>
                <div className="text-sm text-neutral-600 mt-2 line-clamp-2">{p.intro}</div>
                <div className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-widest">
                  Öffnen <ArrowRight size={12} weight="bold" />
                </div>
              </Link>
            ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <FAQ />
      </section>
    </main>
  );
}
